import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useExams from '@/data/hook/exam/useExams';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import CoursePath from '@/models/CoursePath';
import { ROUTES } from '@/utils/routes';

import { examFieldsToDTO } from '@/utils/exam/examFieldsToDTO';

import ExamCard from './ExamCard';
import { ExamFormFields } from './ExamForm';
import ExamFormList from './ExamFormList';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type ExamsTabContentProps = {
  coursePath: CoursePath;
};

export default function ExamsTabContent({ coursePath }: ExamsTabContentProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { courseLineId, moduleId, slug } = usePedWorkspace();
  const { navigate } = useCourseTreeNavigate();
  const {
    exams,
    isLoading,
    createExam,
    isCreating,
    deleteExam,
    reorderExams,
    isReordering,
  } = useExams({ coursePath });

  const { optimistic: optimisticExams, handleDragEnd } = useOptimisticReorder({
    items: exams,
    isReordering,
    onReorder: reorderExams,
  });

  const handleEdit = (examId: number) => {
    if (!slug) return;
    navigate(
      ROUTES.ADMIN.COURSE_LINES.EXAM(courseLineId, moduleId, slug, examId),
    );
  };

  const handleCreateSubmit = (data: ExamFormFields) =>
    createExam(examFieldsToDTO(data));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="relative">
      <DragDropList
        groupId={`exams-list-${coursePath.id}`}
        onDragEnd={handleDragEnd}
        disabled={isReordering || isLimitedEdition}
        className="gap-0"
      >
        {optimisticExams.map((exam, index) => (
          <ExamCard
            key={exam.id}
            exam={exam}
            ordinal={index + 1}
            onEdit={handleEdit}
            onDelete={deleteExam}
            isDraggable
            id={String(exam.id)}
            index={index}
            className="mb-6"
          />
        ))}
      </DragDropList>
      <ExamFormList
        coursePath={coursePath}
        onSubmit={handleCreateSubmit}
        courseLineId={courseLineId}
        isLoading={isCreating}
      />
    </div>
  );
}
